import { useState, type ReactNode } from 'react';

import { api } from '../../api';
import { Aviso, Campo, Painel } from '../../comum';
import type { EtapaTeste } from '../../tipos';
import type { PropsSecao } from './comum';

/**
 * Leitura direta do MySQL do appliance.
 *
 * Opcional e fora de contrato: a Quest documenta a API REST, não o esquema do
 * banco. Destrava CVE, patches faltantes e categoria de software — três coisas
 * que a API não entrega. A conexão é somente-leitura, imposta deste lado.
 */
export function SecaoBanco({ c, podeEditar, acao, salvar }: PropsSecao): ReactNode {
  const [banco, setBanco] = useState({ ...c.bancoKace, senha: '' });
  const [testeBanco, setTesteBanco] = useState<readonly EtapaTeste[] | null>(null);

  return (
    <Painel
      titulo="Banco do KACE (leitura direta)"
      sub="Opcional. Destrava vulnerabilidades por CVE, quais patches faltam e categoria de software — três coisas que a API do appliance não entrega."
    >
      <Aviso tipo="info">
        A API REST é contrato: a Quest documenta e mantém. O banco não — o esquema pode mudar numa
        atualização, sem aviso. Ligue sabendo o que isso compra e o que custa. A conexão é{' '}
        <strong>somente leitura</strong>, imposta pelo próprio produto: qualquer comando que não
        seja consulta é recusado antes de sair daqui.
      </Aviso>

      <label className="alternar">
        <input
          type="checkbox"
          checked={banco.ativo}
          disabled={!podeEditar}
          onChange={(e) => setBanco({ ...banco, ativo: e.target.checked })}
        />
        <span>Ler dados diretamente do banco do appliance</span>
      </label>

      <div className="grade2">
        <Campo rotulo="Endereço" ajuda="Host ou IP do appliance.">
          <input
            value={banco.host}
            disabled={!podeEditar}
            onChange={(e) => setBanco({ ...banco, host: e.target.value })}
          />
        </Campo>
        <Campo rotulo="Porta" ajuda="3306 na instalação padrão.">
          <input
            type="number"
            value={banco.porta}
            disabled={!podeEditar}
            onChange={(e) => setBanco({ ...banco, porta: Number(e.target.value) })}
          />
        </Campo>
        <Campo
          rotulo="Usuário"
          ajuda="Conta de relatórios do KACE. Não precisa (nem deve) ter escrita."
        >
          <input
            value={banco.usuario}
            disabled={!podeEditar}
            onChange={(e) => setBanco({ ...banco, usuario: e.target.value })}
          />
        </Campo>
        <Campo
          rotulo="Senha"
          ajuda={
            c.bancoKace.senhaDefinida
              ? 'Já existe uma senha gravada. Deixe em branco para mantê-la.'
              : 'Nenhuma senha gravada.'
          }
        >
          <input
            type="password"
            value={banco.senha}
            disabled={!podeEditar}
            placeholder={c.bancoKace.senhaDefinida ? '••••••••' : ''}
            onChange={(e) => setBanco({ ...banco, senha: e.target.value })}
          />
        </Campo>
        <Campo
          rotulo="Esquema da organização"
          ajuda='Como "ORG3". Muda por organização do appliance.'
        >
          <input
            value={banco.esquema}
            disabled={!podeEditar}
            placeholder="ORG3"
            onChange={(e) => setBanco({ ...banco, esquema: e.target.value.toUpperCase() })}
          />
        </Campo>
      </div>

      <div className="linha">
        <button
          className="encolhe"
          disabled={acao.executando || !podeEditar}
          onClick={() =>
            void acao.rodar(async () => {
              const r = await api.post<{
                ok: boolean;
                etapas: readonly EtapaTeste[];
              }>('/api/config/banco-kace/testar', banco);
              setTesteBanco(r.etapas);
              return null;
            })
          }
        >
          Testar conexão
        </button>
        <button
          className="primario encolhe"
          disabled={acao.executando || !podeEditar}
          onClick={() => salvar('bancoKace', banco, 'Banco do KACE')}
        >
          Salvar
        </button>
        <span className="cresce" />
      </div>

      {testeBanco !== null && (
        <ul className="passos">
          {testeBanco.map((e) => (
            <li key={e.etapa}>
              <span className="marca-passo">{e.ok ? '✓' : '✗'}</span>
              <span>{e.detalhe}</span>
            </li>
          ))}
        </ul>
      )}
    </Painel>
  );
}
